// ─────────────────────────────────────────────────────────────────────────────
// Per-role page (nav tab) visibility, React port of role-perms.js ROLE_TABS +
// getVisibleTabs + _prefixedParent. Decodes the legacy data-tab keys to the
// React route page-keys:
//   legacy 'releaseshub' → 'releases'  (the Releases catalog)
//   legacy 'releases'    → 'marketing' (the Marketing page, the confusing one)
//   legacy 'schedule'    → 'schedule'  (rendered as Booking or Calendar via canSeeBooking)
//
// Fails CLOSED: any unknown role resolves to the band_member shape (the most
// restrictive sane set), never the artist default. Source of truth stays
// role-perms.js; when a role lands, mirror it in BOTH.
// ─────────────────────────────────────────────────────────────────────────────

import type { User } from './types';

export type DashTabKey =
  | 'dashboard'
  | 'releases'
  | 'content'
  | 'marketing'
  | 'schedule'
  | 'assets'
  | 'contacts';

// The four canonical tab sets (decoded from role-perms.js ROLE_TABS).
const FULL: ReadonlyArray<DashTabKey> = [
  'dashboard', 'releases', 'content', 'marketing', 'schedule', 'assets', 'contacts',
]; // artist / band_member_av / manager(+mgmt_) / label(+label_) / jedi
const ORG_BOOKING: ReadonlyArray<DashTabKey> = ['dashboard', 'schedule', 'assets', 'contacts']; // booking(+booking_) / tour_manager / production / plain band_member
const CONTENT: ReadonlyArray<DashTabKey> = ['dashboard', 'content', 'marketing', 'assets', 'schedule', 'contacts']; // content roles (NO releases hub)
const MERCH: ReadonlyArray<DashTabKey> = ['dashboard', 'schedule', 'contacts']; // merch manager (NO assets)

/**
 * Resolve the effective role key for tab gating on the artist surface. Order:
 *   jedi (god mode) → owner (is_band_primary → artist) → seat (membership_role)
 *   → coarse account role. Mirrors role-perms.js getEffectiveRole intent.
 * The jedi view-as override (applyViewAs) has already swapped role +
 * membership_role + is_band_primary on the user, so this reads the simulated
 * seat transparently.
 */
export function effectiveRoleKey(user: User | null | undefined): string {
  if (!user) return 'band_member';
  const r = String(user.role || '').toLowerCase();
  if (r === 'jedi' || r === 'admin') return r;
  if (user.is_band_primary) return 'artist';
  const m = String(user.membership_role || '').toLowerCase();
  if (m) return m;
  if (r === 'artist' || r === 'band_member_av') return 'band_member_av';
  return r || 'band_member';
}

/** Legacy _prefixedParent: org sub-roles collapse to their parent. */
function prefixedParent(role: string): string | null {
  if (role.startsWith('label_')) return 'label';
  if (role.startsWith('mgmt_')) return 'manager';
  if (role.startsWith('booking_')) return 'booking';
  if (role.startsWith('production_')) return 'foh_engineer';
  return null;
}

// Effective-role → tab set. Mirrors role-perms.js ROLE_TABS (decoded).
const TABS_BY_ROLE: Readonly<Record<string, ReadonlyArray<DashTabKey>>> = {
  jedi: FULL,
  admin: FULL,
  artist: FULL,
  band_member_av: FULL,
  manager: FULL,
  management: FULL,
  label: FULL,
  booking: ORG_BOOKING,
  booking_agent: ORG_BOOKING,
  tour_manager: ORG_BOOKING,
  foh_engineer: ORG_BOOKING,
  monitor_engineer: ORG_BOOKING,
  tech: ORG_BOOKING,
  crew: ORG_BOOKING,
  crew_member: ORG_BOOKING,
  band_member: ORG_BOOKING,
  merch_manager: MERCH,
  content_creator: CONTENT,
  social_media_manager: CONTENT,
  content: CONTENT,
  /* acpro-teams 2026-07-27 — Pro crew seats. All get the restricted
     dashboard/schedule/assets/contacts shape (same as existing crew);
     production_manager mirrors tour_manager. */
  personal_assistant: ORG_BOOKING,
  security: ORG_BOOKING,
  personal_trainer: ORG_BOOKING,
  foh_systems_tech: ORG_BOOKING,
  monitor_systems_tech: ORG_BOOKING,
  rf_tech: ORG_BOOKING,
  pa_tech_1: ORG_BOOKING,
  pa_tech_2: ORG_BOOKING,
  audio_comms_tech: ORG_BOOKING,
  backline: ORG_BOOKING,
  playback: ORG_BOOKING,
  driver: ORG_BOOKING,
  production_manager: ORG_BOOKING,
  merch_assistant: MERCH,
};

/** The set of page-keys this user's effective role may see in the nav. */
export function getVisibleTabs(user: User | null | undefined): ReadonlySet<DashTabKey> {
  const role = effectiveRoleKey(user);
  const explicit = TABS_BY_ROLE[role];
  if (explicit) return new Set(explicit);
  const parent = prefixedParent(role);
  if (parent && TABS_BY_ROLE[parent]) return new Set(TABS_BY_ROLE[parent]);
  // Fail closed to the band_member shape (dashboard/schedule/assets/contacts).
  return new Set(ORG_BOOKING);
}

/** Convenience: may this user see a given page-key? */
export function canSeeTab(user: User | null | undefined, tab: DashTabKey): boolean {
  return getVisibleTabs(user).has(tab);
}
